import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Avatar,
  Breadcrumbs,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ChevronRight, ChevronLeft, Warning } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import StudentNavBar from "app/components/StudentNavBar";
import { colors } from "styles/colors";
import images from "assets/images/images";
import FAQAccordion from "app/components/FAQAccordion";
import PaymentProcessingOverlay from "app/components/PaymentProcessingOverlay";
import { GET_SERVICE_PROVIDER, INITIATE_SERVICE_PROVIDER_PURCHASE } from "app/graphql/services";
import { ServiceProviderResponse, ServiceProvider, ServiceProviderPricing } from "types/services";
import getStore from "store/zustandStore";
import {
  openPaymentPopup,
  validatePaymentFormHTML,
  isPopupBlocked,
  PaymentPopupResult
} from "utils/paymentPopup";
import { clarityEvent } from "utils/hooks/useClarity";
import { getUserInfo } from "utils/helpers";

// Hardcoded identifier for Clovemind service provider
const SERVICE_PROVIDER_IDENTIFIER = 'clovemind';

const StudentSelfcareDetailsComponent = () => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedPricing, setSelectedPricing] = useState<ServiceProviderPricing | null>(null);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("Processing your payment...");
  const [popupBlockedDialog, setPopupBlockedDialog] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const setLoading = getStore((state: any) => state.setLoading);
  const userInfo = getUserInfo();

  // Get service provider details
  const { data, loading, error } = useQuery<ServiceProviderResponse>(GET_SERVICE_PROVIDER, {
    variables: { identifier: SERVICE_PROVIDER_IDENTIFIER },
    fetchPolicy: "no-cache", // Force fresh fetch to get latest data
    errorPolicy: "all"
  });

  // Payment mutation
  const [initiateServiceProviderPurchase, { loading: paymentLoading }] = useMutation(INITIATE_SERVICE_PROVIDER_PURCHASE);

  // Select the first active pricing when data loads
  React.useEffect(() => {
    if (data?.service_provider?.active_pricings && !selectedPricing) {
      const firstPricing = data.service_provider.active_pricings.find(p => p.is_active);
      if (firstPricing) {
        setSelectedPricing(firstPricing);
      }
    }
  }, [data, selectedPricing]);

  // Track page view when pricing is loaded
  React.useEffect(() => {
    if (selectedPricing && userInfo.id) {
      clarityEvent("selfcare_details_viewed", {
        userId: userInfo.id,
        serviceName: selectedPricing.service_name || "1:1 Virtual Counselling Session",
        price: selectedPricing.total_amount,
        isFree: (selectedPricing.total_amount <= 0) ? "true" : "false"
      });
    }
  }, [selectedPricing, userInfo.id]);

  React.useEffect(() => {
    // Never set global loading during any operation to prevent double loaders
    setLoading(false);
  }, [setLoading]);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);

    if (isExpanded && userInfo.id) {
      const faqItem = FAQData.find(faq => faq.id === panel);
      if (faqItem) {
        clarityEvent("selfcare_faq_toggled", {
          userId: userInfo.id,
          questionId: panel,
          question: faqItem.question
        });
      }
    }
  };

  const handlePopupBlockedDialogClose = () => {
    setPopupBlockedDialog(false);
  };

  const handleTryAgain = () => {
    if (userInfo.id) {
      clarityEvent("selfcare_payment_retry_after_block", {
        userId: userInfo.id
      });
    }
    setPopupBlockedDialog(false);
    // Small delay to let user see the dialog closing
    setTimeout(() => {
      handlePayNow();
    }, 500);
  };

  const handlePayNow = async () => {
    if (!serviceProvider || !selectedPricing) return;

    const isFreePricing = selectedPricing.total_amount <= 0;

    // Track payment initiation
    if (userInfo.id) {
      clarityEvent("selfcare_payment_initiated", {
        userId: userInfo.id,
        serviceName: selectedPricing.service_name || "1:1 Virtual Counselling Session",
        amount: selectedPricing.total_amount,
        pricingId: selectedPricing.id,
        isFree: isFreePricing ? "true" : "false"
      });
    }

    if (isFreePricing) {
      // For free services, redirect to success page directly
      enqueueSnackbar("Successfully enrolled in the free service!", { variant: "success" });
      history.push('/student-selfcare/payment/success');
      return;
    }

    // For paid services, initiate payment with popup
    try {
      // Step 1: Check if popups are blocked BEFORE any payment processing
      if (isPopupBlocked()) {
        if (userInfo.id) {
          clarityEvent("selfcare_payment_popup_blocked", {
            userId: userInfo.id,
            amount: selectedPricing.total_amount
          });
        }
        setPopupBlockedDialog(true);
        return;
      }

      // Step 2: Set loading state after popup check
      setIsPaymentProcessing(true);

      // Step 3: Call initiate_service_provider_purchase mutation
      const { data } = await initiateServiceProviderPurchase({
        variables: {
          input: {
            service_provider_pricing_id: selectedPricing.id,
            payment_gateway: "ccavenue"
          }
        }
      });

      const response = data?.initiate_service_provider_purchase;
      
      console.log('Service payment initiation response:', response);

      // Step 4: Check for errors
      if (response?.errors && response.errors.length > 0) {
        setIsPaymentProcessing(false);
        enqueueSnackbar(`Payment initiation failed: ${response.errors.join(', ')}`, { variant: "error" });
        return;
      }

      // Step 5: Validate payment form HTML
      if (!response?.payment_form_html || !validatePaymentFormHTML(response.payment_form_html)) {
        setIsPaymentProcessing(false);
        enqueueSnackbar("Invalid payment form received", { variant: "error" });
        return;
      }
      
      // Log the payment form to check callback URLs
      console.log('Payment form HTML received:', response.payment_form_html);
      
      // Extract and log the action URL from the form
      const actionMatch = response.payment_form_html.match(/action=["']([^"']+)["']/i);
      if (actionMatch) {
        console.log('Payment form action URL:', actionMatch[1]);
      }
      
      // Check if form contains callback/redirect URLs
      const redirectUrlMatch = response.payment_form_html.match(/redirect_url[^>]*value=["']([^"']+)["']/i);
      const cancelUrlMatch = response.payment_form_html.match(/cancel_url[^>]*value=["']([^"']+)["']/i);
      
      if (redirectUrlMatch) {
        console.log('Redirect URL in form:', redirectUrlMatch[1]);
      }
      if (cancelUrlMatch) {
        console.log('Cancel URL in form:', cancelUrlMatch[1]);
      }

      // Step 6: Open payment popup and show processing overlay
      setPaymentMessage("Opening payment window...");
      
      const paymentResult: PaymentPopupResult = await openPaymentPopup(
        response.payment_form_html,
        {
          width: 600,
          height: 700,
          centerOnScreen: true
        }
      );

      // Update processing message
      setPaymentMessage("Verifying payment status...");

      // Step 7: Handle payment result based on server response
      console.log('[PAYMENT_DEBUG] StudentSelfcare - RECEIVED paymentResult from popup:', paymentResult);
      console.log('[PAYMENT_DEBUG] StudentSelfcare - paymentResult.success:', paymentResult.success);
      console.log('[PAYMENT_DEBUG] StudentSelfcare - paymentResult.redirectType:', paymentResult.redirectType);
      console.log('[PAYMENT_DEBUG] StudentSelfcare - paymentResult.status:', paymentResult.status);
      console.log('[PAYMENT_DEBUG] StudentSelfcare - paymentResult.error:', paymentResult.error);
      console.log('[PAYMENT_DEBUG] StudentSelfcare - Full paymentResult object:', paymentResult);
      
      setIsPaymentProcessing(false);
      
      // Check if this is a popup blocked error - if so, show dialog instead of redirecting
      if (paymentResult.error && paymentResult.error.includes('Popup blocked')) {
        console.log('[PAYMENT_DEBUG] StudentSelfcare - Popup blocked detected, showing dialog');
        setPopupBlockedDialog(true);
        return;
      }
      
      // Use redirectType from server response if available, otherwise fallback to success flag
      const redirectType = paymentResult.redirectType || (paymentResult.success ? 'success' : 'failed');
      console.log('[PAYMENT_DEBUG] StudentSelfcare - redirectType determination:');
      console.log('[PAYMENT_DEBUG] StudentSelfcare - paymentResult.redirectType =', paymentResult.redirectType);
      console.log('[PAYMENT_DEBUG] StudentSelfcare - fallback logic = paymentResult.success ?', paymentResult.success, '? success : failed');
      console.log('[PAYMENT_DEBUG] StudentSelfcare - FINAL redirectType =', redirectType);
      
      if (redirectType === 'success') {
        console.log('[PAYMENT_DEBUG] StudentSelfcare - Taking SUCCESS redirect path');
        setPaymentMessage("Payment successful! Redirecting...");
        
        // Build success URL with available parameters
        const successParams = new URLSearchParams();
        if (paymentResult.purchaseId) successParams.set('purchase_id', paymentResult.purchaseId);
        if (paymentResult.transactionId) successParams.set('transaction_id', paymentResult.transactionId);
        if (paymentResult.amount) successParams.set('amount', paymentResult.amount.toString());
        
        const successUrl = `/student-selfcare/payment/success${successParams.toString() ? '?' + successParams.toString() : ''}`;
        console.log('[PAYMENT_DEBUG] StudentSelfcare - Redirecting to SUCCESS URL:', successUrl);
        history.push(successUrl);
        
      } else if (redirectType === 'cancelled') {
        console.log('[PAYMENT_DEBUG] StudentSelfcare - Taking CANCELLED redirect path');
        setPaymentMessage("Payment cancelled...");
        
        // Build cancelled URL with available parameters
        const cancelledParams = new URLSearchParams();
        if (paymentResult.transactionId) cancelledParams.set('transaction_id', paymentResult.transactionId);
        if (paymentResult.error) cancelledParams.set('error', paymentResult.error);
        
        const cancelledUrl = `/student-selfcare/payment/cancelled${cancelledParams.toString() ? '?' + cancelledParams.toString() : ''}`;
        console.log('[PAYMENT_DEBUG] StudentSelfcare - Redirecting to CANCELLED URL:', cancelledUrl);
        history.push(cancelledUrl);
        
      } else {
        console.log('[PAYMENT_DEBUG] StudentSelfcare - Taking FAILURE redirect path (else clause)');
        // All other cases (failed, error, etc.) go to failure page
        setPaymentMessage("Payment failed...");
        
        // Handle special case of user closing popup
        if (paymentResult.error && paymentResult.error.includes('closed')) {
          console.log('[PAYMENT_DEBUG] StudentSelfcare - Special case: user closed popup, going to cancelled');
          history.push('/student-selfcare/payment/cancelled?error=user_cancelled');
        } else {
          console.log('[PAYMENT_DEBUG] StudentSelfcare - Normal failure case, going to failure page');
          
          // Build failure URL with available parameters
          const failureParams = new URLSearchParams();
          if (paymentResult.transactionId) failureParams.set('transaction_id', paymentResult.transactionId);
          if (paymentResult.purchaseId) failureParams.set('purchase_id', paymentResult.purchaseId);
          if (paymentResult.error) failureParams.set('error', paymentResult.error);
          
          const failureUrl = `/student-selfcare/payment/failure${failureParams.toString() ? '?' + failureParams.toString() : ''}`;
          console.log('[PAYMENT_DEBUG] StudentSelfcare - Redirecting to FAILURE URL:', failureUrl);
          history.push(failureUrl);
        }
      }
    } catch (error: any) {
      setIsPaymentProcessing(false);
      enqueueSnackbar(`Payment error: ${error.message}`, { variant: "error" });
    }
  };

  const FAQData = [
    {
      id: 'panel1',
      question: "Should I see a therapist?",
      answer: "Absolutely, therapy isn't just for crisis moments. Therapy isn't only for those with mental health diagnoses; it's a proactive, evidence-based way to boost emotional well-being, resilience, and personal growth."
    },
    {
      id: 'panel2', 
      question: "How often are sessions for therapy?",
      answer: "Therapy is often weekly or bi-weekly, but it really depends on you. Your therapist will help find a pace that fits your goals and progress."
    },
    {
      id: 'panel3',
      question: "Is everything I say in therapy confidential?",
      answer: "Yes. Your privacy is fully respected and your records will not be shared with anybody including your institution and Prosculpt. Therapists only break confidentiality if there's a risk of harm to you or someone else."
    },
    {
      id: 'panel4',
      question: "Can a therapist prescribe medication?",
      answer: "No, therapists cannot prescribe medication. Only psychiatrists, who are medical doctors have the authority to prescribe medication."
    },
    {
      id: 'panel5',
      question: "Is medication a substitute for therapy?",
      answer: "They serve different purposes. Therapy builds lasting emotional skills; medication can help with symptom relief. In some cases, a combination of both is most effective."
    }
  ]

  // Handle loading and error states
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.service_provider) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {error?.message || "Service provider not found"}
        </Alert>
      </Box>
    );
  }

  const serviceProvider = data.service_provider;
  if (!selectedPricing && serviceProvider.active_pricings.length > 0) {
    // This will be handled by the useEffect above
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedPricing) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          No active pricing plans available for this service
        </Alert>
      </Box>
    );
  }

  // Get service and pricing details
  const serviceName = selectedPricing.service_name || serviceProvider.name || '1:1 Virtual Counselling Session';
  const originalPrice = selectedPricing.original_price || 0;
  const discountAmount = selectedPricing.discount_amount || 0;
  const displayPrice = selectedPricing.effective_price || 0; // Final price (what user pays)
  const taxAmount = selectedPricing.gst_amount || 0;
  const grandTotal = selectedPricing.total_amount || 0; // Final amount including GST
  
  // Debug: Log the pricing data to see what's being returned
  console.log('Selected pricing data:', selectedPricing);
  console.log('base_amount_excluding_gst:', selectedPricing.base_amount_excluding_gst);
  
  // Calculate subtotal with fallback logic
  let subtotal = selectedPricing.base_amount_excluding_gst;
  
  // If base_amount_excluding_gst is not available, calculate it from total - gst
  if (subtotal === null || subtotal === undefined) {
    subtotal = grandTotal - taxAmount;
    console.log('Using fallback subtotal calculation (total - gst):', subtotal);
  } else if (subtotal === 0 && grandTotal > 0) {
    // If it's explicitly 0 but we have a grand total, this might be an API issue
    subtotal = grandTotal - taxAmount;
    console.log('base_amount_excluding_gst is 0, using fallback:', subtotal);
  } else {
    console.log('Using API provided base_amount_excluding_gst:', subtotal);
  }
  
  const hasDiscount = selectedPricing.has_discount || false;
  const isFreePricing = grandTotal <= 0;

  return (
    <>
      <PaymentProcessingOverlay 
        open={isPaymentProcessing}
        courseName={serviceName}
        message={paymentMessage}
      />
      <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      <Box sx={{ px: 0, pb: 4 }}>
        {/* Navigation */}
        <Box sx={{ mb: 3 }}>
          {/* Mobile - Go Back button */}
          <Box 
            sx={{ 
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                '& .back-text': {
                  color: colors.primaryBlue,
                }
              }
            }}
            onClick={() => {
              if (userInfo.id) {
                clarityEvent("selfcare_breadcrumb_clicked", {
                  userId: userInfo.id,
                  destination: "selfcare_landing"
                });
              }
              history.push('/student-selfcare');
            }}
          >
            <ChevronLeft 
              sx={{ 
                color: colors.primaryBlue, 
                fontSize: '40px', 
                width: 24,       
                height: 24,
                mr: 1
              }} 
            />
            <Typography
              className="back-text"
              sx={{
                color: colors.fontLighter2,
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              Go Back
            </Typography>
          </Box>

          {/* Desktop - Breadcrumbs */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Breadcrumbs
              separator={<ChevronRight 
                sx={{ 
                  color: colors.primaryBlue, 
                  fontSize: '40px', 
                  width: 40,       
                  height: 40
                }} 
              />}
              aria-label="breadcrumb"
            >
              <Typography
                sx={{
                  color: colors.fontLighter2,
                  fontSize: '16px',
                  fontWeight: 400,
                  cursor: 'pointer',
                  '&:hover': {
                    color: colors.primaryBlue,
                  }
                }}
                onClick={() => {
                  if (userInfo.id) {
                    clarityEvent("selfcare_breadcrumb_clicked", {
                      userId: userInfo.id,
                      destination: "selfcare_landing"
                    });
                  }
                  history.push('/student-selfcare');
                }}
              >
                Self-Care
              </Typography>
              <Typography
                sx={{
                  color: colors.fontDarkBlue,
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                {serviceName}
              </Typography>
            </Breadcrumbs>
          </Box>
        </Box>

        {/* Main Content Layout */}
        {/* Mobile-Only Title Section */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3, pt: "24px" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={images?.consult}
              alt="Therapist"
              sx={{ 
                width: 80, 
                height: 60, 
                borderRadius: '6px', 
                mr: '14px'
              }}
            />
            <Box>
              <Typography
                sx={{
                  color: colors.primaryBlue,
                  fontWeight: 600,
                  fontSize: "16px",
                  mb: "8px",
                  textTransform: 'uppercase'
                }}
              >
                {serviceName.toUpperCase()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center'}}>
                <Box
                  component="img"
                  src={images.clock}
                  alt="clock"
                  sx={{
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain',
                    mr: 1,
                  }}
                />
                <Typography sx={{ color: "#4B5563", fontSize: '14px' }}>
                  {selectedPricing.duration_minutes || 60} MINS
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={{xs: 3, md: 4}}>
          {/* Combined Left Column - Title and Main Content */}
          <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }} sx={{ pt: {xs: "24px !important", md: "32px !important"} }}>
            {/* Session Header - Hide on mobile since we have mobile-only version */}
            <Box sx={{ mb: {xs: 0, md: 3}, display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: {xs: 0, md: 2} }}>
                <Avatar
                  src={images?.consult}
                  alt="Therapist"
                  sx={{ 
                    width: { xs: 80, md: 115 }, 
                    height: { xs: 60, md: 82 }, 
                    borderRadius: '6px', 
                    mr: { xs: '14px', md: 2 } 
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      color: colors.primaryBlue,
                      fontWeight: 600,
                      fontSize: {xs: "16px", md: '20px'},
                      mb: {xs: "8px", md: '12px'},
                      textTransform: 'uppercase'
                    }}
                  >
                    {serviceName.toUpperCase()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center'}}>
                    <Box
                      component="img"
                      src={images.clock}
                      alt="clock"
                      sx={{
                        width: '20px',
                        height: '20px',
                        objectFit: 'contain',
                        mr: 1,
                      }}
                    />
                    <Typography sx={{ color: "#4B5563", fontSize: '14px' }}>
                      {selectedPricing.duration_minutes || 60} MINS
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography
                sx={{
                  color: colors.fontGrey,
                  fontSize: '16px',
                  lineHeight: 1.6,
                  fontWeight: 500,
                  display: { xs: 'none', md: 'block' }
                }}
              >
                Your guide covering some basics of therapy:
              </Typography>
              <Typography
                sx={{
                  color: colors.fontGrey,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  fontWeight: 400,
                  mb: 3,
                  display: { xs: 'none', md: 'block' }
                }}
              >
                There are just a few things we want you to know about therapy.
              </Typography>
            </Box>

            {/* Guide text - Show on mobile above Goal of Therapy */}
            <Typography
              sx={{
                color: colors.fontGrey,
                fontSize: '16px',
                lineHeight: 1.6,
                fontWeight: 500,
                display: { xs: 'block', md: 'none' },
                mb: 1
              }}
            >
              Your guide covering some basics of therapy:
            </Typography>
            <Typography
              sx={{
                color: colors.fontGrey,
                fontSize: '14px',
                lineHeight: 1.6,
                fontWeight: 400,
                mb: 3,
                display: { xs: 'block', md: 'none' }
              }}
            >
              There are just a few things we want you to know about therapy.
            </Typography>

            {/* Goal of Therapy */}
            <Typography
              sx={{
                color: colors.fontDarkBlue,
                fontWeight: 600,
                fontSize: '20px',
                mb: 1,
              }}
            >
              Goal of Therapy
            </Typography>
            <Typography
              sx={{
                color: colors.fontLight,
                fontSize: '14px',
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              It is to equip you with tools and insight to manage life, relationships, stress, emotions, self-doubt, and feel more in control.
            </Typography>
            <Card sx={{ mb: 4, borderRadius: '16px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }} elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {/* Desktop Layout */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start', flexDirection: 'column', mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: '#DBEAFE',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          flexShrink: 0
                        }}
                      >
                        <Box
                          component="img"
                          src={images.heartIcon}
                          alt="heartIcon"
                          sx={{
                            width: '20px',
                            height: '20px',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: colors.fontDarkBlue,
                            fontWeight: 500,
                            fontSize: '16px',
                            mb: "12px"
                          }}
                        >
                          It's a two-way relationship
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.fontGrey,
                            fontSize: '14px',
                            lineHeight: 1.5
                          }}
                        >
                          You get to show up as you. There's no pressure to be perfect. Your therapist is there to understand, not judge.
                        </Typography>
                      </Box>
                    </Box>

                    {/* Mobile Layout */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                      {/* Image and Title Row */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: '#DBEAFE',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: '8px',
                            flexShrink: 0
                          }}
                        >
                          <Box
                            component="img"
                            src={images.heartIcon}
                            alt="heartIcon"
                            sx={{
                              width: '24px',
                              height: '24px',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: colors.fontDarkBlue,
                            fontWeight: 500,
                            fontSize: '16px'
                          }}
                        >
                          It's a two-way relationship
                        </Typography>
                      </Box>
                      
                      {/* Subtitle Full Width */}
                      <Typography
                        sx={{
                          color: colors.fontGrey,
                          fontSize: '14px',
                          lineHeight: 1.5
                        }}
                      >
                        You get to show up as you. There's no pressure to be perfect. Your therapist is there to understand, not judge.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* Desktop Layout */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start', flexDirection: 'column', mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: '#DCFCE7',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          flexShrink: 0
                        }}
                      >
                        <Box
                          component="img"
                          src={images.leafIcon}
                          alt="leafIcon"
                          sx={{
                            width: '20px',
                            height: '20px',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: colors.fontDarkBlue,
                            fontWeight: 500,
                            fontSize: '16px',
                            mb: '12px'
                          }}
                        >
                          Therapy is an investment
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.fontGrey,
                            fontSize: '14px',
                            lineHeight: 1.5
                          }}
                        >
                          It may not be instant, but over time, therapy helps you grow, feel lighter, and face challenges with clarity. Like any good investment, it will pay off if you give it time.
                        </Typography>
                      </Box>
                    </Box>

                    {/* Mobile Layout */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                      {/* Image and Title Row */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: '#DCFCE7',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: '8px',
                            flexShrink: 0
                          }}
                        >
                          <Box
                            component="img"
                            src={images.leafIcon}
                            alt="leafIcon"
                            sx={{
                              width: '24px',
                              height: '24px',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: colors.fontDarkBlue,
                            fontWeight: 500,
                            fontSize: '16px'
                          }}
                        >
                          Therapy is an investment
                        </Typography>
                      </Box>
                      
                      {/* Subtitle Full Width */}
                      <Typography
                        sx={{
                          color: colors.fontGrey,
                          fontSize: '14px',
                          lineHeight: 1.5
                        }}
                      >
                        It may not be instant, but over time, therapy helps you grow, feel lighter, and face challenges with clarity. Like any good investment, it will pay off if you give it time.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* What to expect */}
            <Card sx={{ 
              mb: {xs: 1, md: 4}, 
              boxShadow: { xs: 'none', md: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }, 
              borderRadius: '16px',
              backgroundColor: { xs: 'transparent', md: '#fff' }
            }}>
              <CardContent sx={{ px: 2, py: {xs: 0, md: 3} }}>
                <Typography
                  sx={{
                    color: colors.fontDarkBlue,
                    fontWeight: 600,
                    fontSize: '20px',
                    mb: 2
                  }}
                >
                  What to expect from therapy?
                </Typography>
                
                <Box component="ul" sx={{ pl: '1.2rem', mb: 2, listStyle: 'none', m: 0 }}>
                  {[
                    'A safe, non-judgmental space where you can speak your mind.',
                    'A therapist who\'s present, focused, and supportive.',
                    'No lectures, no clichés — just honest, helpful conversations.',
                    'A chance to understand your thoughts and emotions better and build coping skills that work in real life.',
                  ].map((text, index) => (
                    <Box
                      key={index}
                      component="li"
                      sx={{
                        position: 'relative',
                        pl: '1.5rem',
                        mb: index !== 3 ? '8px' : 0,
                        color: colors.fontLight,
                        fontSize: '16px',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '0.6em',
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#14B8A6',
                          borderRadius: '50%',
                          transform: 'translateY(-50%)',
                        },
                      }}
                    >
                      {text}
                    </Box>
                  ))}
                </Box>


              </CardContent>
            </Card>

            {/* How to make virtual therapy work */}
            <Card sx={{ 
              mb: {xs: 1, md: 4}, 
              boxShadow: { xs: 'none', md: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }, 
              borderRadius: '16px',
              backgroundColor: { xs: 'transparent', md: '#fff' }
            }}>
              <CardContent sx={{ py: {xs: 0, md: 3}, px: 2 }}>
                <Typography
                  sx={{
                    color: colors.fontDarkBlue,
                    fontWeight: 600,
                    fontSize: '20px',
                    mb: 2
                  }}
                >
                  How to make virtual therapy work for you?
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      color: colors.fontDarkBlue,
                      fontWeight: 500,
                      fontSize: '16px',
                      mb: 0.5
                    }}
                  >
                    Make your space as comfortable as possible
                  </Typography>
                  <Typography sx={{ color: colors.fontGrey, fontSize: '14px', mb: 3 }}>
                    You can try keep something comforting nearby, like water or a favorite object. And remember: it's your safe space, no matter where you are or how you connect.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      color: colors.fontDarkBlue,
                      fontWeight: 500,
                      fontSize: '16px',
                      mb: 0.5
                    }}
                  >
                    Be yourself!
                  </Typography>
                  <Typography sx={{ color: colors.fontGrey, fontSize: '14px', mb: 2 }}>
                    Feeling uncomfortable with certain emotions is totally normal. When that happens, try opening up to your therapist, they're there to help you work through it.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      color: colors.fontDarkBlue,
                      fontWeight: 500,
                      fontSize: '16px',
                      mb: 0.5
                    }}
                  >
                    Trust the process and maintain consistency
                  </Typography>
                  <Typography sx={{ color: colors.fontGrey, fontSize: '14px', mb: 2 }}>
                    It's essential for you to believe in and trust the process, while also being consistent with the follow-up appointments. Therapy needs some time and patience is the key. It's okay to go at your own pace.
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: colors.fontDarkBlue,
                      fontWeight: 500,
                      fontSize: '16px',
                      mb: 0.5
                    }}
                  >
                    The Essential Practice of Self-Work
                  </Typography>
                  <Typography sx={{ color: colors.fontGrey, fontSize: '14px' }}>
                    Therapy is a team effort, your therapist supports you, but real change happens in the work you do between sessions. Growth comes from applying what you learn, one small, intentional step at a time.
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  color: colors.fontDarkBlue,
                  fontWeight: 600,
                  fontSize: '20px',
                  mb: 3
                }}
              >
                Frequently asked questions
              </Typography>

              <FAQAccordion faqData={FAQData} defaultExpandedIndex={0} />
            </Box>
          </Grid>

          {/* Right Sidebar - Order Details */}

          <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }} sx={{ pt: { xs: "24px !important", md: 0 } }}>
            <Card
              elevation={0}
              sx={{
                backgroundColor: "#F3F7FF",
                p: {xs: 3, md: 6},
                width: "100%"
              }}
            >
              <Typography
                fontWeight={600}
                sx={{
                  color: colors.fontDarkBlue,
                  mb: {xs: 2, md: 4},
                  fontSize: {xs: "16px", md: "20px"}
                }}
              >
                Order details
              </Typography>

              {/* Course Name and Price */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: {xs: 3, md: 6} }}>
                <Typography
                  component="div"
                  sx={{
                    color: colors.fontDarkBlue,
                    fontSize: '16px',
                    maxWidth: '100%', //make this 60% when we show the price
                  }}
                >
                  {serviceName}
                </Typography>
                {/* <Typography
                  fontWeight={600}
                  sx={{
                    color: colors.fontDarkBlue,
                    fontSize: '20px'
                  }}
                >
                  {isFreePricing ? 'Free' : `₹${displayPrice.toLocaleString()}`}
                </Typography> */}
              </Box>

              {/* Summary Section */}
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color: colors.fontDarkBlue,
                  mb: {xs: 2, md: 4},
                  fontSize: {xs: "16px", md: "20px"}
                }}
              >
                Summary
              </Typography>

              {/* Course Price */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: {xs: 1.5, md: 4} }}>
                <Typography sx={{ color: colors.fontGrey, fontSize: {xs: "14px", md: "16px"}, opacity: 0.8 }}>
                  Price:
                </Typography>
                <Typography sx={{ color: colors.fontDarkBlue, fontSize: {xs: "16px", md: "20px"} }}>
                  {/* {isFreePricing ? 'Free' : `₹${displayPrice.toLocaleString()}`} */}
                  ₹677
                </Typography>
              </Box>

              {/* Discounts */}
              {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: {xs: 1.5, md: 4} }}>
                <Typography sx={{ color: colors.fontGrey, fontSize: {xs: "14px", md: "16px"} }}>
                  Discounts:
                </Typography>
                <Typography sx={{ color: colors.red, fontSize: {xs: "16px", md: "20px"} }}>
                  ₹500
                </Typography>
              </Box> */}


              {/* Tax */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: {xs: 1.5, md: 4} }}>
                <Typography sx={{ color: colors.fontGrey, fontSize: {xs: "14px", md: "16px"}, opacity: 0.8 }}>
                  Tax (GST): {selectedPricing.gst_percent}%
                </Typography>
                <Typography sx={{ color: colors.fontDarkBlue, fontSize: {xs: "16px", md: "20px"} }}>
                  {isFreePricing ? '₹0' : `₹${taxAmount.toLocaleString()}`}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Grand Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                <Typography
                  fontWeight={600}
                  sx={{
                    color: '#000000',
                    fontSize: {xs: "16px", md: "20px"}
                  }}
                >
                  Grand Total:
                </Typography>
                <Typography
                  fontWeight={600}
                  sx={{
                    color: '#000000',
                    fontSize: {xs: "16px", md: "20px"}
                  }}
                >
                  {isFreePricing ? 'Free' : `₹${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                </Typography>
              </Box>

              {/* Pay Now Button - Direct payment initiation */}
              <Button
                variant="contained"
                fullWidth
                onClick={handlePayNow}
                disabled={paymentLoading || isPaymentProcessing}
                sx={{
                  height: "34px",
                  fontSize: "14px",
                  fontWeight: 500,
                  px: "24px",
                  py: "8px",
                  borderRadius: "4px",
                  backgroundColor: '#28C76F',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4CAF50',
                  },
                  '&:disabled': {
                    backgroundColor: colors.lighterGray2,
                    color: colors.fontGrey
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(paymentLoading || isPaymentProcessing) && (
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                  )}
                  {paymentLoading || isPaymentProcessing ? (
                    isFreePricing ? 'Enrolling...' : 'Payment Processing...'
                  ) : (
                    isFreePricing ? 'Enroll Now' : 'Pay Now'
                  )}
                </Box>
              </Button>
            </Card>
          </Grid>

        </Grid>
      </Box>


      {/* Popup Blocked Dialog */}
      <Dialog
        open={popupBlockedDialog}
        onClose={handlePopupBlockedDialogClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '8px',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 3, mt:3 }}>
          <Warning sx={{ color: '#FF9800', fontSize: 32 }} />
          <Typography variant="h5" fontWeight={600} color={colors.fontDarkBlue}>
            Popups are Blocked
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 3 }}>
          <Alert 
            severity="warning" 
            sx={{ mb: 3, backgroundColor: '#FFF3E0', border: '1px solid #FFB74D' }}
          >
            <Typography fontWeight={500}>
              Your browser is blocking pop-ups, which is preventing the payment window from opening.<br/>
              Please enable pop-ups and try again.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handlePopupBlockedDialogClose}
            sx={{
              height: "34px",
              fontSize: "14px",
              fontWeight: 500,
              px: "24px",
              py: "8px",
              borderRadius: "4px",
              color: colors.fontLight,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTryAgain}
            variant="contained"
            sx={{
              height: "34px",
              fontSize: "14px",
              fontWeight: 500,
              px: "24px",
              py: "8px",
              borderRadius: "4px",
              backgroundColor: colors.primaryBlue,
              color: '#fff',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: colors.primaryBlueDark,
              }
            }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export function StudentSelfcareDetails() {
  return (
    <StudentNavBar 
      mainComponent={<StudentSelfcareDetailsComponent />}
      properties={{ backgroundColor: '#F9FAFB' }}
    />
  );
}